import React, { useRef, useEffect, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import "./_waveform-player.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBackward,
  faPlay,
  faPause,
  faForward,
} from "@fortawesome/free-solid-svg-icons";

const formWaveSurferOptions = (ref) => ({
  container: ref,
  waveColor: "#666",
  progressColor: "#f04",
  cursorColor: "#f04",
  barWidth: 2,
  barRadius: 2,
  responsive: true,
  height: 40,
  normalize: true,
  partialRender: true,
});

const WaveformPlayer = ({ audioUrl }) => {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!waveformRef.current) return;

    wavesurfer.current = WaveSurfer.create(
      formWaveSurferOptions(waveformRef.current)
    );

    wavesurfer.current.load(audioUrl);

    wavesurfer.current.on("ready", () => {
      setDuration(wavesurfer.current.getDuration());
    });

    wavesurfer.current.on("finish", () => {
      setPlaying(false);
      setCurrentTime(duration);
    });

    wavesurfer.current.on("audioprocess", () => {
      setCurrentTime(wavesurfer.current.getCurrentTime());
    });

    return () => wavesurfer.current.destroy();
  }, [audioUrl, duration]);

  const handlePlayPause = () => {
    wavesurfer.current.playPause();
    setPlaying(!playing);
  };

  const handleRewind = () => {
    const newTime = Math.max(wavesurfer.current.getCurrentTime() - 5, 0);
    wavesurfer.current.seekTo(newTime / duration);
  };

  const handleForward = () => {
    const newTime = Math.min(wavesurfer.current.getCurrentTime() + 5, duration);
    wavesurfer.current.seekTo(newTime / duration);
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="audio-player">
      <div className="controls">
        <button
          onClick={handleRewind}
          className="btn-circle small"
          aria-label="Rewind 5 seconds"
        >
          <FontAwesomeIcon icon={faBackward} />
        </button>
        <button
          onClick={handlePlayPause}
          className="btn-circle large"
          aria-label={playing ? "Pause" : "Play"}
        >
          <FontAwesomeIcon icon={playing ? faPause : faPlay} />
        </button>
        <button
          onClick={handleForward}
          className="btn-circle small"
          aria-label="Forward 5 seconds"
        >
          <FontAwesomeIcon icon={faForward} />
        </button>
      </div>

      <div ref={waveformRef} id="waveform" className="waveform" />

      <div className="time-info">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );
};

export default WaveformPlayer;
